import { ActionIcon, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useCallback, useState } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import clsx from 'clsx';
import { CollapseUnmount } from '~/components/common/collapse/CollapseUnmount';
import { api } from '~/trpc-extra/api';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import OperationControl from '../../OperationControl';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { ConditionalOpInner } from './ConditionalOpInner';
import EditableTextArea from '~/components/common/EditableTextArea';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';
import { CONDITIONAL_PROMPT_MAX_LENGTH } from '~/common/operation/constants';

export default function ConditionalOp({
  operation,
  testContentId,
  step,
  previewCursorAllowed,
  readonly,
  disableMenu,
  parentContexts,
}: {
  operation: SelectedOperation;
  testContentId: string;
  step: SelectedStep;
  previewCursorAllowed: boolean;
  readonly?: boolean;
  disableMenu?: boolean;
  parentContexts?: OperationParentContext[];
}) {
  const trpcUtils = api.useUtils();
  const conditionalBlockId = operation.conditionalOp?.conditionalBlockId;
  const [expanded, setExpanded] = useState(false);

  const parentContext = parentContexts?.at(-1);

  const conditionalForm = useForm({
    initialValues: {
      conditionPrompt: operation.conditionalOp?.conditionPrompt || '',
    },
  });

  const { mutateAsync: upsertOperation } = api.operation.upsertOperation.useMutation({
    onError: () => {
      notifications.show({
        title: 'Error',
        message: 'Failed to update conditional',
        color: 'red',
      });
    },
    onSuccess: () => {
      if (parentContext?.conditional) {
        void trpcUtils.operation.getFullConditionalBlock.invalidate({
          conditionalBlockId: parentContext.conditional.conditionalBlockId,
        });
      } else if (parentContext?.componentId) {
        void trpcUtils.component.get.invalidate({ id: parentContext.componentId });
      } else {
        void trpcUtils.testContent.get.invalidate({ id: testContentId });
      }
    },
  });

  const updateConditionalPrompt = useCallback(
    async (conditionPrompt: string) => {
      await upsertOperation({
        operation: {
          operationId: operation.id,
          stepId: step.id,
          order: operation.order,
          pageAlias: operation.pageAlias,
          skip: operation.skip,
          parentContext,
          operation: {
            operationType: 'CONDITIONAL' as const,
            conditionPrompt,
            conditionalBlockId: conditionalBlockId || '',
          },
        },
      });
    },
    [
      operation.id,
      operation.order,
      operation.pageAlias,
      operation.skip,
      conditionalBlockId,
      step.id,
      parentContext,
      upsertOperation,
    ],
  );

  const handleConditionSave = useCallback(
    async (value: string) => {
      conditionalForm.setFieldValue('conditionPrompt', value);
      await updateConditionalPrompt(value);
    },
    [conditionalForm, updateConditionalPrompt],
  );

  return (
    <div className="flex w-full flex-col items-start py-[0.2rem]">
      <div className="w-full flex-1 rounded border border-gray-200 bg-[var(--mantine-color-body)] dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between rounded bg-gray-50 px-2 py-1 dark:bg-neutral-700/20">
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex flex-1 items-center gap-2">
              <Tooltip
                label="AI will resolve this condition when this step is executed"
                withArrow
                openDelay={500}
                withinPortal={false}
              >
                <div className="flex flex-1 items-center gap-2">
                  <div className="ml-1 text-sm text-purple-700 dark:text-purple-400">If</div>
                  <EditableTextArea
                    initialVal={conditionalForm.values.conditionPrompt}
                    placeHolder="Your condition here..."
                    handleSave={handleConditionSave}
                    readonly={readonly}
                    inputType="input"
                    className="flex-1"
                    inputStyle="border-none bg-transparent text-sm py-0.5 px-1 focus:outline-none focus:border-1 focus:border-blue-600 focus:bg-white"
                    maxTextLength={CONDITIONAL_PROMPT_MAX_LENGTH}
                    saveOnBlur={true}
                  />
                </div>
              </Tooltip>
            </div>

            {/* Expand/Collapse and operation control */}
            <div className="flex items-center gap-2">
              <ActionIcon
                title={expanded ? 'Collapse' : 'Expand'}
                size="xs"
                variant="transparent"
                color="gray"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded((prev) => !prev);
                }}
              >
                <IconChevronDown
                  className={clsx('transition-all duration-200 ease-in-out', {
                    'rotate-180': expanded,
                  })}
                  size={15}
                />
              </ActionIcon>

              {!disableMenu && (
                <div className={clsx({ invisible: readonly })}>
                  <OperationControl
                    operation={operation}
                    previewCursorAllowed={previewCursorAllowed}
                    testContentId={testContentId}
                    stepOrder={step.stepOrder}
                    parentContext={parentContext}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Expandable Content - If/Else Blocks */}
        <CollapseUnmount in={expanded}>
          <div className="w-full border-t border-gray-200 dark:border-gray-700" />
          <ConditionalOpInner
            conditionalBlockId={conditionalBlockId || ''}
            testContentId={testContentId}
            readonly={readonly}
            step={step}
            parentRootOperationId={operation.id}
            parentOperationOrder={operation.order}
            parentContexts={parentContexts}
          />
        </CollapseUnmount>
      </div>
    </div>
  );
}
